import * as React from "react";
import { ChevronDownIcon, MonitorIcon, MoonIcon, SunIcon } from "lucide-react";

import { useTranslations } from "@/i18n";
import { cn } from "@/shared/lib/utils";
import { useTheme } from "@/shared/theme/components/use-theme";
import { ThemeMode } from "@/shared/theme/lib/types";

const MODE_ICON: Record<ThemeMode, React.ReactNode> = {
  [ThemeMode.System]: <MonitorIcon className='size-4' />,
  [ThemeMode.Light]: <SunIcon className='size-4' />,
  [ThemeMode.Dark]: <MoonIcon className='size-4' />,
};

const MODE_ORDER: ThemeMode[] = [ThemeMode.System, ThemeMode.Light, ThemeMode.Dark];

/**
 * Compact theme selector rendered inside the Clerk UserProfile "Preferences" page.
 * Uses a native <select> so the menu always layers above the Clerk modal and
 * inherits the OS color-scheme. Reuses the existing ThemeProvider via useTheme();
 * selecting a mode persists it and applies it across the app immediately.
 */
export function ThemePreferencePanel({ embedded = false }: { embedded?: boolean } = {}) {
  const t = useTranslations("settings.themeModeSwitcher");
  const { config, setMode } = useTheme();

  return (
    <div className='space-y-5'>
      {embedded ? null : (
        <>
          <h1 className='text-xl font-semibold tracking-tight text-foreground'>{t("pageLabel")}</h1>
          <div className='border-t border-border' />
        </>
      )}

      <div className='flex items-center justify-between gap-4'>
        <span className='text-sm font-medium text-foreground'>{t("appearance")}</span>

        <div className='relative'>
        <span className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground'>
          {MODE_ICON[config.mode]}
        </span>
        <select
          aria-label={t("appearance")}
          value={config.mode}
          onChange={(event) => setMode(event.target.value as ThemeMode)}
          className={cn(
            "h-9 cursor-pointer appearance-none rounded-lg border border-border bg-card pr-9 pl-9 text-sm font-medium text-foreground",
            "focus-visible:ring-ring focus-visible:ring-offset-background transition-colors hover:bg-accent/40 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
          )}
        >
          {MODE_ORDER.map((mode) => (
            <option key={mode} value={mode}>
              {t(`mode.${mode}` as never)}
            </option>
          ))}
        </select>
          <ChevronDownIcon className='text-muted-foreground pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2' />
        </div>
      </div>
    </div>
  );
}
